import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, Loading, StatCard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { DollarSign, Clock, BarChart3, Building2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => { const r = await api.get("/reports/overview"); setData(r.data); })();
  }, []);

  if (!data) return <Loading />;

  const catColors = ["#2563EB", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981"];

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Training completion, department performance, hours and costs." testid="reports-header" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Training Hours" value={data.total_hours} icon={Clock} testid="report-hours" />
        <StatCard label="Total Cost" value={`$${data.total_cost.toLocaleString()}`} icon={DollarSign} accent="text-emerald-600" testid="report-cost" />
        <StatCard label="Categories" value={data.by_category.length} icon={BarChart3} testid="report-categories" />
        <StatCard label="Departments" value={data.by_department.length} icon={Building2} testid="report-departments" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Completion by Category</h3>
          <p className="text-xs text-muted-foreground mb-4">Enrolled vs completed across training categories</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_category} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="category" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Bar dataKey="enrolled" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.4} />
              <Bar dataKey="completed" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Completions by Department</h3>
          <p className="text-xs text-muted-foreground mb-4">Completed training per department</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_department} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <YAxis type="category" dataKey="department" width={110} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Bar dataKey="completed" radius={[0, 4, 4, 0]}>
                {data.by_department.map((_, i) => <Cell key={i} fill={catColors[i % catColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
